import { googleAuthService } from "./googleAuth";

export interface EmailMessage {
  id: string;
  threadId: string;
  subject: string;
  from: string;
  to: string[];
  cc?: string[];
  bcc?: string[];
  snippet: string;
  date: string;
  isRead: boolean;
  isStarred: boolean;
  hasAttachments: boolean;
  labels: string[];
  sizeEstimate: number;
  historyId: string;
  internalDate: string;
}

export interface EmailThread {
  id: string;
  historyId: string;
  messages: EmailMessage[];
  snippet: string;
}

export interface EmailLabel {
  id: string;
  name: string;
  messageListVisibility: string;
  labelListVisibility: string;
  type: string;
  messagesTotal?: number;
  messagesUnread?: number;
  threadsTotal?: number;
  threadsUnread?: number;
}

export interface EmailConnectionStatus {
  isConnected: boolean;
  lastChecked: Date;
  error?: string;
  hasPermissions: boolean;
  profile?: {
    emailAddress: string;
    messagesTotal: number;
    threadsTotal: number;
    historyId: string;
  };
}

class EmailService {
  private connectionStatus: EmailConnectionStatus = {
    isConnected: false,
    lastChecked: new Date(),
    hasPermissions: false,
  };

  // Check email connection status
  async checkConnectionStatus(): Promise<EmailConnectionStatus> {
    try {
      console.log("EmailService: Checking connection status...");

      // Note: Backend token validation is done by GoogleServicesService before calling this
      // Attempt Gmail API call - will fail if tokens are invalid or permissions are missing
      const response = await googleAuthService.makeAuthenticatedRequest("https://gmail.googleapis.com/gmail/v1/users/me/messages?maxResults=1");

      if (response.ok) {
        // Also get profile information
        const profileResponse = await googleAuthService.makeAuthenticatedRequest("https://gmail.googleapis.com/gmail/v1/users/me/profile");

        let profile;
        if (profileResponse.ok) {
          profile = await profileResponse.json();
        }

        this.connectionStatus = {
          isConnected: true,
          lastChecked: new Date(),
          hasPermissions: true,
          profile,
        };
      } else {
        this.connectionStatus = {
          isConnected: false,
          lastChecked: new Date(),
          hasPermissions: false,
          error: `Gmail API error: ${response.status}`,
        };
      }
    } catch (error) {
      console.error("EmailService: Connection check failed:", error);
      this.connectionStatus = {
        isConnected: false,
        lastChecked: new Date(),
        hasPermissions: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }

    return this.connectionStatus;
  }

  // Get emails
  async getEmails(maxResults = 10, query?: string, labelIds?: string[]): Promise<EmailMessage[]> {
    try {
      console.log("EmailService: Fetching emails...");

      let url = `https://gmail.googleapis.com/gmail/v1/users/me/messages?maxResults=${maxResults}`;

      if (query) {
        url += `&q=${encodeURIComponent(query)}`;
      }

      if (labelIds && labelIds.length > 0) {
        url += `&labelIds=${labelIds.map((id) => encodeURIComponent(id)).join(",")}`;
      }

      const response = await googleAuthService.makeAuthenticatedRequest(url);

      if (!response.ok) {
        throw new Error(`Gmail API error: ${response.status}`);
      }

      const data = await response.json();
      const emails: EmailMessage[] = [];

      // Get detailed info for each email
      for (const message of data.messages || []) {
        const detailResponse = await googleAuthService.makeAuthenticatedRequest(`https://gmail.googleapis.com/gmail/v1/users/me/messages/${message.id}`);

        if (detailResponse.ok) {
          const detail = await detailResponse.json();
          const headers = detail.payload?.headers || [];

          emails.push({
            id: message.id,
            threadId: detail.threadId,
            subject: headers.find((h: any) => h.name === "Subject")?.value || "No Subject",
            from: headers.find((h: any) => h.name === "From")?.value || "Unknown",
            to:
              headers
                .find((h: any) => h.name === "To")
                ?.value?.split(",")
                .map((email: string) => email.trim()) || [],
            cc:
              headers
                .find((h: any) => h.name === "Cc")
                ?.value?.split(",")
                .map((email: string) => email.trim()) || [],
            bcc:
              headers
                .find((h: any) => h.name === "Bcc")
                ?.value?.split(",")
                .map((email: string) => email.trim()) || [],
            snippet: detail.snippet || "",
            date: headers.find((h: any) => h.name === "Date")?.value || "",
            isRead: !detail.labelIds?.includes("UNREAD"),
            isStarred: detail.labelIds?.includes("STARRED") || false,
            hasAttachments: detail.payload?.parts?.some((part: any) => part.filename) || false,
            labels: detail.labelIds || [],
            sizeEstimate: detail.sizeEstimate || 0,
            historyId: detail.historyId,
            internalDate: detail.internalDate,
          });
        }
      }

      console.log(`EmailService: Fetched ${emails.length} emails`);
      return emails;
    } catch (error) {
      console.error("EmailService: Failed to fetch emails:", error);
      throw error;
    }
  }

  // Get unread emails
  async getUnreadEmails(maxResults = 10): Promise<EmailMessage[]> {
    return this.getEmails(maxResults, "is:unread");
  }

  // Get starred emails
  async getStarredEmails(maxResults = 10): Promise<EmailMessage[]> {
    return this.getEmails(maxResults, "is:starred");
  }

  // Get emails from a specific sender
  async getEmailsFromSender(sender: string, maxResults = 10): Promise<EmailMessage[]> {
    return this.getEmails(maxResults, `from:${sender}`);
  }

  // Get email by ID
  async getEmail(messageId: string): Promise<EmailMessage> {
    try {
      console.log(`EmailService: Fetching email ${messageId}...`);

      const response = await googleAuthService.makeAuthenticatedRequest(`https://gmail.googleapis.com/gmail/v1/users/me/messages/${messageId}`);

      if (!response.ok) {
        throw new Error(`Gmail API error: ${response.status}`);
      }

      const detail = await response.json();
      const headers = detail.payload?.headers || [];

      console.log("EmailService: Email fetched successfully");

      return {
        id: detail.id,
        threadId: detail.threadId,
        subject: headers.find((h: any) => h.name === "Subject")?.value || "No Subject",
        from: headers.find((h: any) => h.name === "From")?.value || "Unknown",
        to:
          headers
            .find((h: any) => h.name === "To")
            ?.value?.split(",")
            .map((email: string) => email.trim()) || [],
        cc:
          headers
            .find((h: any) => h.name === "Cc")
            ?.value?.split(",")
            .map((email: string) => email.trim()) || [],
        bcc:
          headers
            .find((h: any) => h.name === "Bcc")
            ?.value?.split(",")
            .map((email: string) => email.trim()) || [],
        snippet: detail.snippet || "",
        date: headers.find((h: any) => h.name === "Date")?.value || "",
        isRead: !detail.labelIds?.includes("UNREAD"),
        isStarred: detail.labelIds?.includes("STARRED") || false,
        hasAttachments: detail.payload?.parts?.some((part: any) => part.filename) || false,
        labels: detail.labelIds || [],
        sizeEstimate: detail.sizeEstimate || 0,
        historyId: detail.historyId,
        internalDate: detail.internalDate,
      };
    } catch (error) {
      console.error("EmailService: Failed to fetch email:", error);
      throw error;
    }
  }

  // Get email thread
  async getThread(threadId: string): Promise<EmailThread> {
    try {
      console.log(`EmailService: Fetching thread ${threadId}...`);

      const response = await googleAuthService.makeAuthenticatedRequest(`https://gmail.googleapis.com/gmail/v1/users/me/threads/${threadId}`);

      if (!response.ok) {
        throw new Error(`Gmail API error: ${response.status}`);
      }

      const thread = await response.json();
      const messages: EmailMessage[] = [];

      for (const message of thread.messages || []) {
        const headers = message.payload?.headers || [];

        messages.push({
          id: message.id,
          threadId: message.threadId,
          subject: headers.find((h: any) => h.name === "Subject")?.value || "No Subject",
          from: headers.find((h: any) => h.name === "From")?.value || "Unknown",
          to:
            headers
              .find((h: any) => h.name === "To")
              ?.value?.split(",")
              .map((email: string) => email.trim()) || [],
          cc:
            headers
              .find((h: any) => h.name === "Cc")
              ?.value?.split(",")
              .map((email: string) => email.trim()) || [],
          bcc:
            headers
              .find((h: any) => h.name === "Bcc")
              ?.value?.split(",")
              .map((email: string) => email.trim()) || [],
          snippet: message.snippet || "",
          date: headers.find((h: any) => h.name === "Date")?.value || "",
          isRead: !message.labelIds?.includes("UNREAD"),
          isStarred: message.labelIds?.includes("STARRED") || false,
          hasAttachments: message.payload?.parts?.some((part: any) => part.filename) || false,
          labels: message.labelIds || [],
          sizeEstimate: message.sizeEstimate || 0,
          historyId: message.historyId,
          internalDate: message.internalDate,
        });
      }

      console.log("EmailService: Thread fetched successfully");

      return {
        id: thread.id,
        historyId: thread.historyId,
        messages,
        snippet: thread.snippet,
      };
    } catch (error) {
      console.error("EmailService: Failed to fetch thread:", error);
      throw error;
    }
  }

  // Get labels
  async getLabels(): Promise<EmailLabel[]> {
    try {
      console.log("EmailService: Fetching labels...");

      const response = await googleAuthService.makeAuthenticatedRequest("https://gmail.googleapis.com/gmail/v1/users/me/labels");

      if (!response.ok) {
        throw new Error(`Gmail API error: ${response.status}`);
      }

      const data = await response.json();
      const labels: EmailLabel[] = (data.labels || []).map((label: any) => ({
        id: label.id,
        name: label.name,
        messageListVisibility: label.messageListVisibility,
        labelListVisibility: label.labelListVisibility,
        type: label.type,
        messagesTotal: label.messagesTotal,
        messagesUnread: label.messagesUnread,
        threadsTotal: label.threadsTotal,
        threadsUnread: label.threadsUnread,
      }));

      console.log(`EmailService: Fetched ${labels.length} labels`);
      return labels;
    } catch (error) {
      console.error("EmailService: Failed to fetch labels:", error);
      throw error;
    }
  }

  // Send email
  async sendEmail(to: string[], subject: string, body: string, cc?: string[], bcc?: string[]): Promise<void> {
    try {
      console.log("EmailService: Sending email...");

      const email = [
        `To: ${to.join(", ")}`,
        `Subject: ${subject}`,
        ...(cc && cc.length > 0 ? [`Cc: ${cc.join(", ")}`] : []),
        ...(bcc && bcc.length > 0 ? [`Bcc: ${bcc.join(", ")}`] : []),
        "",
        body,
      ].join("\r\n");

      const encodedEmail = btoa(email).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");

      const response = await googleAuthService.makeAuthenticatedRequest("https://gmail.googleapis.com/gmail/v1/users/me/messages/send", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          raw: encodedEmail,
        }),
      });

      if (!response.ok) {
        throw new Error(`Gmail API error: ${response.status}`);
      }

      console.log("EmailService: Email sent successfully");
    } catch (error) {
      console.error("EmailService: Failed to send email:", error);
      throw error;
    }
  }

  // Mark email as read/unread
  async markAsRead(messageId: string, read = true): Promise<void> {
    try {
      console.log(`EmailService: Marking email ${messageId} as ${read ? "read" : "unread"}...`);

      const response = await googleAuthService.makeAuthenticatedRequest(`https://gmail.googleapis.com/gmail/v1/users/me/messages/${messageId}/modify`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          removeLabelIds: read ? ["UNREAD"] : [],
          addLabelIds: read ? [] : ["UNREAD"],
        }),
      });

      if (!response.ok) {
        throw new Error(`Gmail API error: ${response.status}`);
      }

      console.log("EmailService: Email marked successfully");
    } catch (error) {
      console.error("EmailService: Failed to mark email:", error);
      throw error;
    }
  }

  // Star/unstar email
  async toggleStar(messageId: string, starred = true): Promise<void> {
    try {
      console.log(`EmailService: ${starred ? "Starring" : "Unstarring"} email ${messageId}...`);

      const response = await googleAuthService.makeAuthenticatedRequest(`https://gmail.googleapis.com/gmail/v1/users/me/messages/${messageId}/modify`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          removeLabelIds: starred ? [] : ["STARRED"],
          addLabelIds: starred ? ["STARRED"] : [],
        }),
      });

      if (!response.ok) {
        throw new Error(`Gmail API error: ${response.status}`);
      }

      console.log("EmailService: Email starred/unstarred successfully");
    } catch (error) {
      console.error("EmailService: Failed to toggle star:", error);
      throw error;
    }
  }

  // Delete email
  async deleteEmail(messageId: string): Promise<void> {
    try {
      console.log(`EmailService: Deleting email ${messageId}...`);

      const response = await googleAuthService.makeAuthenticatedRequest(`https://gmail.googleapis.com/gmail/v1/users/me/messages/${messageId}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        throw new Error(`Gmail API error: ${response.status}`);
      }

      console.log("EmailService: Email deleted successfully");
    } catch (error) {
      console.error("EmailService: Failed to delete email:", error);
      throw error;
    }
  }

  // Get current connection status
  getConnectionStatus(): EmailConnectionStatus {
    return { ...this.connectionStatus };
  }

  // Force check connection status
  async forceCheck(): Promise<EmailConnectionStatus> {
    return await this.checkConnectionStatus();
  }

  // Request email permissions
  async requestPermissions(): Promise<void> {
    try {
      console.log("EmailService: Requesting email permissions...");
      await googleAuthService.requestCalendarPermissions();
    } catch (error) {
      console.error("EmailService: Failed to request permissions:", error);
      throw error;
    }
  }
}

// Create singleton instance
export const emailService = new EmailService();
